import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import LoadingSpinner from "../LoadingSpinner";
import Grid from "../Grid";
import { checkupValidation } from "../validations/vehicleCreation";
import { getTextByValueAndKey } from "../../api/keyvalue";
import ButtonRenderer from "../renderer/ButtonRenderer";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import Button from "../input/Button";
import { AUTH_TYPES, FORMS, MODULES } from "../../const";
import { isNumber } from "../../helper";
import DeleteButtonRenderer from "../renderer/DeleteButtonRenderer";

const FORM = FORMS.VEHICLE.CHECKUP.form;

const VehicleCheckups = forwardRef(
  ({ dropdown, isLoading, editMode, currentValues }, ref) => {
    const [isAddLoading, setIsAddLoading] = useState(false); //newly added
    const [currentCheckup, setCurrentCheckup] = useState({}); //newly added
    const [buttonClicked, setButtonClicked] = useState(false); //newly added
    const [deletedList, setDeletedList] = useState([]); //newly added

    const [checkupsData, setCheckupsData] = useState([]); //newly added
    const [checkupsCount, setCheckupsCount] = useState(2); //newly added

    const [checkups, setCheckups] = useState({
      vckCode: "", //doesn't exist in front end
      vckReference: "",
      vckType: "",
      vckTypeValue: "", //doesn't exist in front end
      vckValue: "", //newly added
      vckDate: new Date().toISOString().split("T")[0],
      vckExpiry: "",
      vckMillage: "",
      vckDescription: "",
      vckCondition: "",
      vckConditionValue: "", //doesn't exist in front end
      vckNextMillage: "", //doesn't exist in front end
      vckStatus: 2, //doesn't exist in front end
      vckActive: 1,
      vckActiveValue: "", //doesn't exist in front end
    });

    const [nextCheckup, setNextCheckup] = useState("");
    const [nextCheckupType, setNextCheckupType] = useState("");
    const [vckTypeSet, setVckTypeSet] = useState(false);

    const [currentErrors, setCurrentErrors] = useState({});

    useEffect(() => {
      if (checkups?.vckType !== null) {
        const selected = dropdown?.vckTypeOpt?.filter(
          (item) => item.key === checkups.vckType,
        )[0];
        if (selected !== undefined) {
          setNextCheckup(selected.keyValue2);
          setNextCheckupType(selected.keyValue3);
        }
      }
    }, [checkups.vckType, dropdown.vckTypeOpt, vckTypeSet]);

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentCheckups = [];
          let id = 2;
          for (let currentValue of currentValues) {
            currentCheckups.push({
              ...currentValue,
              vpyVehicle: currentValue.vpyVehicleId,
              id: id,
            });
            id++;
          }
          setCheckupsCount(id);
          setCheckupsData(currentCheckups);
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (buttonClicked) {
        setCheckups({
          vckCode: "", //doesn't exist in front end
          vckReference: "",
          vckType: "",
          vckTypeValue: "", //doesn't exist in front end
          vckValue: "", //newly added
          vckDate: new Date().toISOString().split("T")[0],
          vckExpiry: "",
          vckMillage: "",
          vckDescription: "",
          vckCondition: "",
          vckConditionValue: "", //doesn't exist in front end
          vckNextMillage: "", //doesn't exist in front end
          vckStatus: 2, //doesn't exist in front end
          vckActive: 1,
          vckActiveValue: "", //doesn't exist in front end
        });
        setCurrentErrors({});
        setButtonClicked(false);
        setCurrentCheckup({});
      }
    }, [buttonClicked]);

    // console.log(checkups);

    useEffect(() => {
      if (Object.keys(currentCheckup).length > 0) {
        setCheckups({
          ...currentCheckup,
          vckDate: currentCheckup.vckDate
              ? currentCheckup.vckDate.split("T")[0]
              : "",
          vckExpiry: currentCheckup.vckExpiry
              ? currentCheckup.vckExpiry.split("T")[0]
              : "",
        });
      }
    }, [currentCheckup, editMode]);

    useImperativeHandle(ref, () => ({
      resetGrid: () => {
        setCheckupsData([]);
      },
      // getData: () => (checkupsData.length > 0 ? checkupsData[0] : checkups),
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            unmodified: [],
            created: [],
            deleted: deletedList,
          };

          checkupsData.forEach((checkup) => {
            if (checkup.vckId && checkup.vckId !== null) {
              if (checkup.modified) {
                updateState.modified.push(checkup);
              } else {
                updateState.unmodified.push(checkup);
              }
            } else {
              updateState.created.push(checkup);
            }
          });

          return updateState;
        } else {
          return checkupsData;
        }
      },
      validateForm: () => {
        return checkupsData.length <= 0;
      },
      hasValidRevenueLicenseExpiry: (vckTypeOpt = []) => {
        return checkupsData.some((c) => {
          let typeText = c.vckTypeValue || "";
          if (!typeText && c.vckType && Array.isArray(vckTypeOpt)) {
            const opt = vckTypeOpt.find(
              (o) =>
                String(o.value) === String(c.vckType) ||
                String(o.key) === String(c.vckType)
            );
            if (opt) typeText = opt.text || opt.title || "";
          }
          const isRevenueLicense = /revenue/i.test(typeText);
          return (
            isRevenueLicense &&
            c.vckExpiry &&
            String(c.vckExpiry).trim() !== ""
          );
        });
      },
    }));

    const onAddClick = async () => {
      setIsAddLoading(true);

      const checkupsErrors = checkupValidation(checkups);

      if (Object.keys(checkupsErrors).length === 0) {
        const vckTypeValue = await getTextByValueAndKey(
          "veh_check",
          checkups.vckType,
        );
        const vckConditionValue = await getTextByValueAndKey(
          "veh_check_condition",
          checkups.vckCondition,
        );
        const vckActiveValue = await getTextByValueAndKey(
          "active",
          checkups.vckActive,
        );

        if (!checkups.vckId || checkups.vckId === null) {
          if (!checkups.id || checkups.id === null) {
            setCheckupsData((prev) => [
              ...prev,
              {
                ...checkups,
                vckTypeValue,
                vckConditionValue,
                vckActiveValue,
                id: checkupsCount,
              },
            ]);

            setCheckupsCount((prev) => prev + 1);
          } else {
            setCheckupsData((prev) =>
              prev.filter((checkup) => checkup.id !== checkups.id),
            );
            setCheckupsData((prev) => [
              ...prev,
              {
                ...checkups,
                vckTypeValue,
                vckConditionValue,
                vckActiveValue,
              },
            ]);
          }
        } else {
          setCheckupsData((prev) =>
            prev.filter((checkup) => checkup.vckId !== checkups.vckId),
          );
          setCheckupsData((prev) => [
            ...prev,
            {
              ...checkups,
              vckTypeValue,
              vckConditionValue,
              vckActiveValue,
              modified: true,
            },
          ]);
        }
        setButtonClicked(true);
      }
      setCurrentErrors(checkupsErrors);
      document.querySelector("#vehicle-details-form").classList.add("error");
      setIsAddLoading(false);
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Checkups</h4>
              <div className="two fields">
                <Dropdown
                  required={true}
                  label="Type"
                  error={currentErrors.vckType}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_type"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.vckTypeOpt}
                  placeholder="Type"
                  style={{ height: "20px" }}
                  value={checkups.vckType}
                  onChange={(e, data) => {
                    setCheckups((prev) => ({ ...prev, vckType: data.value }));
                    setVckTypeSet(true);
                  }}
                />

                <Input
                  label="Reference"
                  error={currentErrors.vckReference}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_reference"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckReference: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="reference"
                  placeholder="Reference"
                  value={checkups.vckReference}
                  onChange={(e) =>
                    setCheckups((prev) => ({
                      ...prev,
                      vckReference: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="three fields">
                <div className="field">
                  <label>Next Checkup</label>
                  <input
                    type="number"
                    name="next_checkup"
                    placeholder="Next Checkup"
                    defaultValue={nextCheckup}
                    disabled={true}
                  />
                  {/* <small style={{ color: "#9f3a38" }}>{`${
                  errors.vckReference ? errors.vckReference : ""
                }`}</small> */}
                </div>
                <div className="field">
                  <label>Next Checkup Type</label>
                  <input
                    type="text"
                    name="next_checkup_type"
                    placeholder="Next Checkup Type"
                    defaultValue={nextCheckupType}
                    disabled={true}
                  />
                  {/* <small style={{ color: "#9f3a38" }}>{`${
                  errors.vckReference ? errors.vckReference : ""
                }`}</small> */}
                </div>
                <Input
                  label="Value"
                  error={currentErrors.vckValue}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_value"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckValue: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="value"
                  placeholder="Value"
                  value={checkups.vckValue}
                  onChange={(e) =>
                    setCheckups((prev) => ({
                      ...prev,
                      vckValue: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="two fields">
                <Input
                  label="Checkup Date"
                  error={currentErrors.vckDate}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_date"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckDate: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  type="date"
                  name="date"
                  placeholder="Date"
                  value={checkups.vckDate}
                  onChange={(e) =>
                    setCheckups((prev) => ({
                      ...prev,
                      vckExpiry: "",
                      vckDate: e.target.value,
                    }))
                  }
                />
                <Input
                  required={true}
                  label="Expiry / Next action date"
                  error={currentErrors.vckExpiry}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_expiry"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckExpiry: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  type="date"
                  name="exp_date"
                  placeholder="Expiry Date"
                  min={checkups.vckDate ? checkups.vckDate : new Date().toISOString().split("T")[0]}
                  value={checkups.vckExpiry}
                  disabled={
                    checkups.vckDate === null || checkups.vckDate === ""
                  }
                  onChange={(e) => {
                    if (e.target.value < checkups.vckDate) {
                      setCurrentErrors((prevState) => ({
                        ...prevState,
                        vckExpiry: "Can not be before Date",
                      }));
                      return;
                    } else {
                      setCurrentErrors((prevState) => ({
                        ...prevState,
                        vckExpiry: "",
                      }));
                    }
                    setCheckups((prev) => ({
                      ...prev,
                      vckExpiry: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <Input
                  label="Mileage"
                  error={currentErrors.vckMillage}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_millage"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckMillage: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="Mileage"
                  placeholder="Mileage"
                  value={checkups.vckMillage}
                  onChange={(e) =>
                    setCheckups((prev) => ({
                      ...prev,
                      vckMillage: parseInt(e.target.value),
                    }))
                  }
                />
                <Input
                  label="Description"
                  error={currentErrors.vckDescription}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_description"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckDescription: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="description"
                  placeholder="Description"
                  value={checkups.vckDescription}
                  onChange={(e) =>
                    setCheckups((prev) => ({
                      ...prev,
                      vckDescription: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="two fields">
                <Dropdown
                  required={true}
                  label="Condition"
                  error={currentErrors.vckCondition}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_condition"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckCondition: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.vckConditionOpt}
                  placeholder="Condition"
                  style={{ height: "20px" }}
                  value={checkups.vckCondition}
                  onChange={(e, data) =>
                    setCheckups((prev) => ({
                      ...prev,
                      vckCondition: data.value,
                    }))
                  }
                />
                {/* <Dropdown
                  label="Active"
                  error={currentErrors.vckActive}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vck_condition"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vckCondition: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.activeOpt}
                  placeholder="Active"
                  style={{ height: "20px" }}
                  value={checkups.vckActive}
                  onChange={(e, data) =>
                    setCheckups((prev) => ({
                      ...prev,
                      vckActive: data.value,
                    }))
                  }
                /> */}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  template="reset"
                  onClick={(e) => {
                    setButtonClicked(true);
                  }}
                />
                <Button
                  template="addOrSave"
                  authenticated={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={`${FORM}`}
                  create={Object.keys(currentCheckup).length === 0}
                  onClick={onAddClick}
                  isLoading={isAddLoading}
                />
              </div>
              <Grid
                heading="Checkup List"
                config={[
                  { headerName: "Reference", field: "vckReference" },
                  { headerName: "Type", field: "vckTypeValue" },
                  { headerName: "Date",
                    field: "vckDate",
                    valueFormatter: (params) =>
                        params.value ? params.value.split("T")[0] : ""},
                  { headerName: "Expiry", field: "vckExpiry",
                    valueFormatter: (params) =>
                        params.value ? params.value.split("T")[0] : ""},
                  { headerName: "Millage", field: "vckMillage" },
                  { headerName: "Description", field: "vckDescription" },
                  { headerName: "Condition", field: "vckConditionValue" },
                  { headerName: "Active", field: "vckActiveValue" },
                  {
                    field: "Edit",
                    filter: true,
                    cellRenderer: ButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.CREATE,
                      clickHandler: () => {
                        setCurrentCheckup(params.data);
                      },
                      label: "Edit",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode: editMode,
                    }),
                  },
                  {
                    field: "Delete",
                    filter: true,
                    cellRenderer: DeleteButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.DELETE,
                      clickHandler: () => {
                        if (params.data.vckId && params.data.vckId !== null) {
                          setDeletedList((prev) => [
                            ...prev,
                            params.data.vckId,
                          ]);
                        }

                        setCheckupsData((prev) =>
                          prev.filter(
                            (checkup) => checkup.id !== params.data.id,
                          ),
                        );
                      },
                      message:
                              "Are you sure you want to delete this Vehicle Checkup?",
                      label: "Delete",
                      className: "ui red button",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode:
                        params.data.vckId !== null &&
                        isNumber(params.data.vckId),
                    }),
                  },
                ]}
                content={checkupsData}
              />
            </div>
          </form>
          </>
        )}
      </>
    );
  },
);

export default VehicleCheckups;
